<template>
  <div id="editable-assessment">
    <a-progress :percent="percentageCompleted" />
    <a-steps :current="current" style="margin-bottom: 10px" size="small" v-if="steps">
      <a-step
        v-for="(item, itemIndex) in steps"
        :key="item.title + assessmentId"
        :title="item.title"
        @click="handleStepClick(itemIndex)"
        :status="checkStepStatus(item, itemIndex)"
        :id="item.title + assessmentId"
      >
      </a-step>
    </a-steps>
    <a-divider v-if="steps[current]">{{ steps[current].subTitle }}</a-divider>
    <a-collapse accordion v-if="steps[current]">
      <a-collapse-panel
        :key="tabIndex + '_' + current"
        v-for="(tab, tabIndex) in steps[current].content"
        :forceRender="false"
        :class="
          tab.allAnswered !== undefined && !tab.allAnswered
            ? 'has-unanswered'
            : 'all-answered-collapse'
        "
      >
        <div
          slot="header"
          :style="tab.allAnswered !== undefined && !tab.allAnswered ? 'color: red;' : ''"
        >
          {{ tab.heading }}
        </div>
        <a-list itemLayout="horizontal" :dataSource="tab.questions">
          <a-list-item slot="renderItem" slot-scope="item, questionIndex">
            <a-list-item-meta>
              <h3 slot="title" :class="canAnswer(item) ? 'disabled-question' : ''">
                <span>
                  <a-icon
                    type="check-circle"
                    theme="twoTone"
                    twoToneColor="#52c41a"
                    v-if="
                      getAssessmentValue(current + 1, tabIndex + 1, questionIndex + 1)
                        .length > 0
                    "
                  />
                  <a-icon
                    type="close-circle"
                    theme="twoTone"
                    :twoToneColor="canAnswer(item) ? '#ff0b0b9c' : '#e23d3d'"
                    v-else
                  />
                </span>
                {{ current + 1 + "." + (tabIndex + 1) + "." + (questionIndex + 1) + "." }}
                {{ item.question }}
              </h3>
              <div slot="description" :style="{ margin: '20px' }">
                <a-radio-group
                  slot="description"
                  :defaultValue="
                    getAssessmentValue(current + 1, tabIndex + 1, questionIndex + 1)
                  "
                  buttonStyle="solid"
                  size="default"
                  :name="`question_${current + 1}_${tabIndex + 1}_${questionIndex + 1}`"
                  @change="handleQuestionChecked"
                >
                  <a-radio-button
                    :value="option.replace(' ', '_').toLowerCase()"
                    v-for="(option, optionIndex) in item.options"
                    :key="optionIndex"
                    :disabled="canAnswer(item)"
                    :class="isAdminOnly(item)"
                  >
                    {{ option }}
                  </a-radio-button>
                </a-radio-group>
              </div>
            </a-list-item-meta>
          </a-list-item>
        </a-list>
        <a-divider />
        <div class="domain-assessment" style="text-align: center" v-if="isTestCourse">
          <span>
            <a-icon
              type="check-circle"
              theme="twoTone"
              twoToneColor="#52c41a"
              v-if="getDefaultDomainScore(current + 1, tabIndex + 1).length > 0"
            />
            <a-icon type="close-circle" theme="twoTone" twoToneColor="#e23d3d" v-else />
          </span>
          Overall:
          <a-radio-group
            slot="description"
            :defaultValue="getDefaultDomainScore(current + 1, tabIndex + 1)"
            buttonStyle="solid"
            size="small"
            :name="getDomainRadioName(current + 1, tabIndex + 1)"
            @change="handleDomainValueChange"
          >
            <a-radio-button
              :value="domainScoreValue.key"
              v-for="(domainScoreValue, domainScoreKey) in sortDomainScore(
                tab.domainScore
              )"
              :key="domainScoreKey"
              :disabled="canAnswerDomain(tab.questions)"
            >
              {{ domainScoreValue.value }}
            </a-radio-button>
          </a-radio-group>
        </div>
      </a-collapse-panel>
    </a-collapse>
    <div class="steps-action" style="margin-top: 10px">
      <a-button v-if="current > 0" style="float: left" @click="saveAssessments(), prev()">
        Previous
      </a-button>
      <a-button
        v-if="current < Object.keys(steps).length - 1"
        type="primary"
        @click="saveAssessments(), next()"
        style="float: right"
      >
        Next
      </a-button>
    </div>
    <div
      class="comment"
      :style="{ marginTop: '20px', clear: 'both', paddingTop: '10px' }"
    >
      <label v-if="hasComments('standard_' + current + '_comment')">
        Comments: <br />
        <a-alert
          v-if="shouldShowInterimAlert('standard_' + current + '_comment')"
          type="info"
          style="margin: 8px 0px"
        >
          <div
            style="white-space: pre-wrap"
            slot="message"
            v-html="
              getMarkedComment(
                'Interim-Reviewer',
                comments['standard_' + current + '_comment']
              )
            "
          ></div>
        </a-alert>
        <a-alert
          v-if="shouldShowAdminAlert('standard_' + current + '_comment')"
          type="info"
          style="margin: 8px 0px"
        >
          <div
            style="white-space: pre-wrap"
            slot="message"
            v-html="
              getMarkedComment(
                'Admin-Reviewer',
                previousComments['admin_review_comment'][
                  'standard_' + current + '_comment'
                ]
              )
            "
          ></div>
        </a-alert>
        <a-alert
          v-if="shouldShowFirstAlert('standard_' + current + '_comment')"
          type="info"
          style="margin: 8px 0px"
        >
          <div
            style="white-space: pre-wrap"
            slot="message"
            v-html="
              getMarkedComment(
                'First-Reviewer',
                previousComments['first_review_comment'][
                  'standard_' + current + '_comment'
                ]
              )
            "
          ></div>
        </a-alert>
        <a-alert
          v-if="shouldShowSecondAlert('standard_' + current + '_comment')"
          type="info"
          style="margin: 8px 0px"
        >
          <div
            style="white-space: pre-wrap"
            slot="message"
            v-html="
              getMarkedComment(
                'Second-Reviewer',
                previousComments['second_review_comment'][
                  'standard_' + current + '_comment'
                ]
              )
            "
          ></div>
        </a-alert>
      </label>
      <label v-if="(isEditable && assessmentStatus !== 'completed') || isTelasTelasAdmin">
        {{ formattedReviewLevel }} Reviewer Comments:
        <a-textarea
          :style="{ marginTop: '10px' }"
          placeholder=""
          autoSize
          v-model="reviewComments['standard_' + current + '_comment']"
          v-if="(isEditable && assessmentStatus !== 'completed') || isTelasTelasAdmin"
        />
      </label>
    </div>
    <a-button-group
      :style="{ width: '100%', marginTop: '20px' }"
      v-if="percentageCompleted >= 100"
    >
      <a-button
        id="download-pdf"
        type="dashed"
        icon="download"
        :loading="isDownloading"
        @click="handleDownloadPDF"
        v-if="assessmentStatus === 'completed'"
      >
        Download PDF
      </a-button>
      <a-popconfirm
        placement="topLeft"
        ok-text="Yes"
        cancel-text="No"
        @confirm="handleUnmarkComplete()"
      >
        <template slot="title">
          <p>Are you sure you want to <b>unmark</b> this assessment as complete?</p>
        </template>
        <a-button
          id="unmark-complete-button"
          type="danger"
          ghost
          v-if="assessmentStatus === 'complete' || assessmentStatus === 'completed'"
        >
          Unmark Complete
        </a-button>
      </a-popconfirm>
      <a href="test" ref="testButton" download v-show="false">Test Button</a>
    </a-button-group>

    <a-button-group
      class="button-group"
      :style="{ width: '100%', marginTop: '20px', textAlign: 'right' }"
      v-if="
        isEditable ||
        reviewLevel === 'admin_reviewer' ||
        userRole === 'telas_telas_administrator'
      "
    >
      <a-button
        id="save-button"
        type="default"
        icon="cloud-download"
        @click="saveAssessments()"
        v-if="
          assessmentStatus !== 'completed' || userRole === 'telas_telas_administrator'
        "
      >
        Save
      </a-button>
      <a-popconfirm
        placement="top"
        ok-text="I'm sure"
        cancel-text="Keep editing"
        @confirm="handleMarkComplete()"
        okType="danger"
      >
        <template slot="title">
          <h3>Are you Sure?</h3>
          <p>
            If you click 'Yes', you will not be able to make any more changes to this
            assessment.
          </p>
        </template>
        <a-button
          id="mark-complete-button"
          type="default"
          icon="check-circle"
          :disabled="percentageCompleted < 100"
          v-if="assessmentStatus !== 'completed'"
          v-show="percentageCompleted >= 100"
        >
          Mark Complete
        </a-button>
      </a-popconfirm>
    </a-button-group>
  </div>
</template>

<script>
/* eslint-disable */
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
// import moment from 'moment';

export default {
  name: "editable-assessment",
  data() {
    return {
      assessmentId: this.$route.params.assessmentId,
      current: 0,
      steps: [],
      reviewAnswers: {},
      percentageCompleted: 0,
      reviewComments: {
        standard_0_comment: "",
        standard_1_comment: "",
        standard_2_comment: "",
        standard_3_comment: "",
        standard_4_comment: "",
        standard_5_comment: "",
        standard_6_comment: "",
        standard_7_comment: "",
      },
      completedQuestion: 0,
      comment: "",
      assessmentStatus: "in-progress",
      totalQuestions: 0,
      isDownloading: false,
    };
  },
  props: [
    "assessmentData",
    "assessmentAnswerData",
    "isEditable",
    "startDate",
    "comments",
    "adminAssessmnetData",
    "reviewLevel",
    "previousComments",
  ],
  computed: {
    isTelasTelasAdmin() {
      return this.userRole === "telas_telas_administrator";
    },
    formattedReviewLevel() {
      return this.reviewLevel
        .replace("_reviewer", "")
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    ...mapGetters("user", {
      userRole: "getUserRole",
      userToken: "getUserToken",
    }),
    ...mapState("assessments", {
      //questions: (state) => state.assessmentQuestions,
      pdfLink: (state) => state.pdfLink,
    }),
    ...mapGetters("assessments", {
      isTestCourse: "isTestCourse",
    }),
    ...mapState("frameworks", {
      questions: (state) => state.frameworkQuestions,
    }),
  },
  created() {
    this.refetchData();
    this.getAssessmentDetail({
      assessmentId: this.$route.params.assessmentId,
    });
    this.unsubscribe = this.$store.subscribe((mutation, state) => {
      if (mutation.type === "assessments/GETASSESSMENTDETAILBYID") {
        // eslint-disable-next-line
        this.percentageCompleted =
          this.assessmentData.percentage_completed !== undefined
            ? Number(this.assessmentData.percentage_completed[0])
            : 0;
        // eslint-disable-next-line
        this.reviewAnswers = this.assessmentAnswerData;
        this.reviewComments = this.comments;
        this.fetchFrameworkQuestions(this.frameworkId);
      }
      if (mutation.type === "frameworks/SET_FRAMEWORK_QUESTIONS") {
        this.steps = Object.values(this.questions);
        this.checkIfAllAnswered();
        this.getTotalQuestions();
      }
    });
  },
  beforeDestroy() {
    this.unsubscribe();
  },
  mounted() {},
  watch: {
    assessmentData(newValue, oldValue) {
      this.courseId = this.assessmentData.assigned_course[0];
      this.fetchFrameworkId();
      this.assessmentStatus =
        this.assessmentData.assessment_status !== undefined
          ? this.assessmentData.assessment_status[0]
          : "in-progress";
    },
  },
  methods: {
    hasComments(commentKey) {
      return (
        this.comments[commentKey] !== "" ||
        this.previousComments["admin_review_comment"][commentKey] !== ""
      );
    },
    shouldShowInterimAlert(commentKey) {
      return (
        this.isTestCourse &&
        this.reviewLevel !== "admin_reviewer" &&
        (this.userRole === "telas_telas_administrator" ||
          this.userRole === "telas_admin_reviewer") &&
        this.comments[commentKey] !==
          this.previousComments["admin_review_comment"][commentKey]
      );
    },
    shouldShowAdminAlert(commentKey) {
      return (
        (this.previousComments["admin_review_comment"][commentKey] &&
          this.reviewLevel !== "admin_reviewer") ||
        (this.userRole !== "telas_telas_administrator" &&
          !/^\s*$/.test(this.previousComments["admin_review_comment"][commentKey]))
      );
    },
    shouldShowFirstAlert(commentKey) {
      return (
        (this.previousComments["first_review_comment"][commentKey] &&
          this.assessmentStatus === "completed" &&
          this.reviewLevel === "first_reviewer") ||
        (this.userRole === "telas_telas_administrator" &&
          !/^\s*$/.test(this.previousComments["first_review_comment"][commentKey]) &&
          !this.isTestCourse)
      );
    },
    shouldShowSecondAlert(commentKey) {
      return (
        (this.previousComments["second_review_comment"][commentKey] &&
          this.assessmentStatus === "completed" &&
          this.reviewLevel === "second_reviewer") ||
        (this.userRole === "telas_telas_administrator" &&
          !/^\s*$/.test(this.previousComments["second_review_comment"][commentKey]) &&
          !this.isTestCourse)
      );
    },
    shouldShowEditableAdminTextarea(commentKey) {
      return (
        this.userRole === "telas_telas_administrator" &&
        this.previousComments["admin_review_comment"][commentKey] ===
          this.comments[commentKey]
      );
    },
    getMarkedComment(prefix, comment) {
      return this.$marked(`${prefix}: <br />${comment}`);
    },
    async fetchFrameworkId() {
      if (!this.courseId || !this.userToken) {
        console.error("Course ID or user token is missing");
        return;
      }
      try {
        const frameworkId = await this.getCourseFramework({
          courseId: this.courseId,
          userToken: this.userToken,
        });
        // Now you have the frameworkId, you can directly call fetchFrameworkQuestions
        // Or store it in your component's state and use it wherever needed
        this.fetchFrameworkQuestions(frameworkId);
      } catch (error) {
        console.error("Failed to fetch framework ID", error);
      }
    },
    async refetchData() {
      await this.fetchFrameworkQuestions(this.frameworkId);
    },
    getDefaultCommentValue(commentKey) {
      return this.reviewComments[commentKey];
    },
    handleStepClick(step) {
      this.current = parseInt(step, 16);
      this.checkIfAllAnswered();
    },
    next() {
      this.current++;
    },
    prev() {
      this.current--;
    },
    handleQuestionChecked(e) {
      const questionKey = e.target.name;
      const { value } = e.target;
      this.reviewAnswers[questionKey] = value;
      this.reviewAnswers = {
        ...this.assessmentAnswerData,
        ...this.reviewAnswers,
      };
      const totalQuestions = this.getTotalQuestions();
      // eslint-disable-next-line
      // this.percentageCompleted = Math.ceil(
      //   (Object.keys(this.reviewAnswers).length / totalQuestions) * 100
      // ).toFixed();
      this.percentageCompleted = Number(
        ((Object.keys(this.reviewAnswers).length / totalQuestions) * 100).toFixed()
      );
      this.checkIfAllAnswered();
    },
    handleDomainValueChange(e) {
      const questionKey = e.target.name;
      const { value } = e.target;
      this.reviewAnswers[questionKey] = value;
      this.reviewAnswers = {
        ...this.assessmentAnswerData,
        ...this.reviewAnswers,
      };
      const totalQuestions = this.getTotalQuestions();
      // eslint-disable-next-line
      this.percentageCompleted = Number(
        ((Object.keys(this.reviewAnswers).length / totalQuestions) * 100).toFixed()
      );
      // console.log(totalQuestions, this.percentageCompleted, Object.keys(this.reviewAnswers).length);
      this.checkIfAllAnswered();
    },
    getTotalDomainQuestions() {
      let totalDomainQuestions = 0;
      if (!this.isTestCourse) return 0;
      this.steps.forEach((element) => {
        element.content.forEach((content) => {
          if (this.userRole === "telas_admin_reviewer") {
            if (this.canAnswerDomain(content.questions)) {
              totalDomainQuestions += 1;
            }
          } else {
            if (!this.canAnswerDomain(content.questions)) {
              totalDomainQuestions += 1;
            }
          }
        });
      });
      return totalDomainQuestions;
    },
    canAnswerDomain(questionsObj) {
      if (!this.isEditable) {
        return true;
      }
      if (this.isEditable && this.assessmentStatus === "completed") {
        return true;
      }
      if (
        this.userRole === "telas_telas_administrator" &&
        this.assessmentStatus !== "completed"
      ) {
        return false;
      }
      const hasNonAdminQuestion = questionsObj.some((item) => !item.adminOnly);
      const hasAdminOnlyQuestion = questionsObj.some((item) => item.adminOnly);
      if (this.userRole === "telas_interim_reviewers") {
        return false;
      }
      if (this.userRole === "telas_admin_reviewers" && this.isTestCourse) {
        return true;
      }
      if (this.userRole === "telas_admin_reviewers") {
        return !hasAdminOnlyQuestion;
      }
      if (this.userRole === "telas_telas_administrator") {
        return !hasAdminOnlyQuestion;
      }
      return !hasNonAdminQuestion;
    },
    getDefaultDomainScore(stepIndex, tabIndex) {
      let domain = this.getDomain(stepIndex);
      if (this.isTestCourse && this.userRole === "telas_interim_reviewers") {
        if (
          !this.canAnswerDomain(this.steps[stepIndex - 1].content[tabIndex - 1].questions)
        ) {
          delete this.adminAssessmnetData[`domain_${domain}_${stepIndex}_${tabIndex}`];
        }
        // this.steps[stepIndex-1].content[tabIndex-1].questions.forEach((question) => {
        //   console.log(question);
        // });
      }
      let reviewData = this.isTestCourse
        ? {
            ...this.assessmentAnswerData,
            ...this.reviewAnswers,
            ...this.adminAssessmnetData,
          }
        : {
            ...this.assessmentAnswerData,
            ...this.reviewAnswers,
          };
      return reviewData[`domain_${domain}_${stepIndex}_${tabIndex}`]
        ? reviewData[`domain_${domain}_${stepIndex}_${tabIndex}`]
        : "";
    },
    getTotalQuestions() {
      return this.totalQuestions + this.getTotalDomainQuestions();
    },
    canAnswer(itemObj) {
      if (!this.isEditable) {
        return true;
      }
      if (this.isEditable && this.assessmentStatus === "completed") {
        return true;
      }
      if (this.userRole === "telas_admin_reviewers") {
        if (itemObj.adminOnly) {
          return false;
        } else {
          return true;
        }
      } else {
        if (itemObj.adminOnly) {
          return true;
        } else {
          return false;
        }
      }
    },
    ...mapActions("courses", {
      getCourseDetails: "getCourseDetails",
      getCourseFramework: "getCourseFramework",
    }),
    ...mapActions("assessments", {
      updateAssessments: "updateAssessmentData",
      getAssessmentDetail: "getAssessmentDetail",
      getAssessmentQuestions: "getAssessmentQuestions",
      downloadPDF: "downloadPDF",
    }),
    ...mapActions("frameworks", {
      fetchFrameworkQuestions: "fetchFrameworkQuestions",
    }),
    ...mapMutations("frameworks", {
      setFrameworkQuestions: "SET_FRAMEWORK_QUESTIONS",
    }),
    ...mapMutations("courses", {
      setCourseDetails: "SETCOURSEDETAIL",
      setCourseFramework: "SETCOURSEFRAMEWORK",
    }),
    saveAssessments() {
      const reviewData = {
        ...this.assessmentAnswerData,
        ...this.reviewAnswers,
      };
      const reviewParams = {
        assessment_data: reviewData,
        assessments_id: this.$route.params.assessmentId,
        percentage_completed: this.percentageCompleted,
        comment: this.reviewComments,
        assessmentStartDate: this.startDate,
        action: "save",
      };
      if (
        this.assessmentStatus !== "completed" ||
        this.userRole === "telas_admin_reviewers"
      ) {
        this.updateAssessments(reviewParams);
      }
    },
    handleUnmarkComplete() {
      const reviewData = {
        ...this.assessmentAnswerData,
        ...this.reviewAnswers,
      };
      this.updateAssessments({
        assessment_data: reviewData,
        assessments_id: this.$route.params.assessmentId,
        percentage_completed: this.percentageCompleted,
        comment: this.reviewComments,
        assessmentStartDate: this.startDate,
        is_test_course: this.isTestCourse,
        action: "uncomplete",
      });
    },
    getAssessmentValue(current, tabIndex, questionIndex) {
      let reviewData = {
        ...this.assessmentAnswerData,
        ...this.adminAssessmnetData,
        ...this.reviewAnswers,
      };
      return reviewData[`question_${current}_${tabIndex}_${questionIndex}`] !== undefined
        ? reviewData[`question_${current}_${tabIndex}_${questionIndex}`]
        : "";
    },
    handleMarkComplete() {
      const reviewData = {
        ...this.assessmentAnswerData,
        ...this.reviewAnswers,
      };
      this.updateAssessments({
        assessment_data: reviewData,
        assessments_id: this.$route.params.assessmentId,
        percentage_completed: this.percentageCompleted,
        comment: this.reviewComments,
        assessmentStartDate: this.startDate,
        is_test_course: this.isTestCourse,
        action: "complete",
      });
    },
    getDomain(step) {
      let domain = 1;
      if (step >= 1 && step <= 2) {
        domain = 1;
      } else if (step > 2 && step <= 4) {
        domain = 2;
      } else if (step >= 5 && step <= 6) {
        domain = 3;
      } else if (step >= 7 && step <= 8) {
        domain = 4;
      }
      return domain;
    },
    getDomainRadioName(step, tabIndex) {
      let domainIndex = this.getDomain(step);
      return `domain_${domainIndex}_${step}_${tabIndex}`;
    },
    checkIfAllAnswered() {
      const totalOtherReviewerQuestions = [];
      const totalAdminReviewerQuestions = [];
      this.steps.forEach((step, stepIndex) => {
        for (let tabIndex = 0; tabIndex < step.content.length; tabIndex++) {
          const contentQuestionValue = [];
          const adminOnlyQuestions = [];
          const otherReviewerQuestions = [];
          this.steps[stepIndex].content[tabIndex].questions.forEach(
            (question, questionIndex) => {
              if (
                this.getAssessmentValue(stepIndex + 1, tabIndex + 1, questionIndex + 1)
                  .length > 0
              ) {
                contentQuestionValue.push(
                  this.getAssessmentValue(stepIndex + 1, tabIndex + 1, questionIndex + 1)
                );
              }
              if (question.adminOnly) {
                adminOnlyQuestions.push(question);
                totalAdminReviewerQuestions.push(question);
              } else {
                otherReviewerQuestions.push(question);
                totalOtherReviewerQuestions.push(question);
              }
              if (this.userRole === "telas_admin_reviewers") {
                if (contentQuestionValue.length === adminOnlyQuestions.length) {
                  this.totalQuestions = totalAdminReviewerQuestions.length;
                  step.content[tabIndex].allAnswered = true;
                } else {
                  step.content[tabIndex].allAnswered = false;
                }
              } else {
                this.totalQuestions = totalOtherReviewerQuestions.length;
                if (question.adminOnly) {
                  otherReviewerQuestions.push(question);
                }
                if (contentQuestionValue.length === otherReviewerQuestions.length) {
                  step.content[tabIndex].allAnswered = true;
                } else {
                  step.content[tabIndex].allAnswered = false;
                }
              }
            }
          );
        }
        const hasUnAnsweredTabs = step.content.some((contentVal) => {
          return contentVal.allAnswered !== true;
        });
        if (hasUnAnsweredTabs) {
          step.hasUnAnsweredTabs = true;
        } else {
          step.hasUnAnsweredTabs = false;
        }
      });
    },
    checkStepStatus(itemObj, index) {
      if (this.current === index) {
        return "process";
      }
      if (!itemObj.hasUnAnsweredTabs || itemObj.hasUnAnsweredTabs === undefined) {
        return "finish";
      } else {
        return "error";
      }
    },
    isAdminOnly(itemObj) {
      // if (! this.isEditable) {
      //   return true;
      // }
      // if (this.userRole === 'telas_admin_reviewers') {
      if (itemObj.adminOnly) {
        return "admin-only-radio";
      } else {
        return "";
      }
      // } else {
      // if (itemObj.adminOnly) {
      //   return 'admin-only-radio';
      // } else {
      //   return '';
      // }
      // }
    },
    sortDomainScore(domainScoreValue) {
      const order = ["Yes", "Yes But", "No But", "No"];
      const newOrder = [];
      Object.entries(domainScoreValue).map((a) => {
        newOrder.push({
          key: a[0],
          value: a[1],
        });
        return "";
      });
      newOrder.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
      return newOrder;
    },
    handleDownloadPDF() {
      this.isDownloading = true;
      this.downloadPDF({
        assessmentId: this.assessmentId,
        userToken: this.userToken,
        isReport: "no",
      }).then(() => {
        this.isDownloading = false;
        const dummyButton = this.$refs.testButton;
        dummyButton.href = this.pdfLink.fileURL;
        dummyButton.target = "_blank";
        dummyButton.click();
      });
    },
  },
};
</script>

<style scoped>
#mark-complete-button:hover {
  border-color: #73d13d;
  background-color: #73d13d;
  color: #ffff;
}

#mark-complete-button {
  color: #73d13d;
}

#save-button:hover {
  border-color: #40a9ff;
  background-color: #40a9ff;
  color: #ffff;
}

.ant-radio-button-wrapper-disabled.ant-radio-button-wrapper-checked {
  background-color: #52c41a !important;
  color: #fff;
  border: gray;
  box-shadow: 0px -1px 4px 1px #c5baba;
  border-radius: 1px;
}

h3.disabled-question {
  color: gray;
}

.ant-radio-button-wrapper-disabled.admin-only-radio.ant-radio-button-wrapper-checked {
  background-color: #3d4a4a !important;
}
</style>
<style>
.ant-steps-item-icon {
  width: 20px !important;
  height: 20px !important;
  line-height: 20px !important;
  font-size: 12px !important;
  vertical-align: middle !important;
}

.ant-steps-item-title {
  font-size: 12px !important;
}

.ant-steps-item-content {
  vertical-align: middle !important;
}

.ant-steps-item:hover {
  cursor: pointer;
}

.has-unanswered .ant-collapse-header {
  color: red !important;
  text-decoration: underline #198ffc;
}
</style>
